/*
 * a11y-carousel.js — accessibility repairs for the Slick carousels.
 * AudioEye Aug 2026: HOME-01/02/03/04/05/11, BULL-65, BULL-67,
 * APVR-79/80/84/87, REEL-165/166/167.
 *
 * Slick is initialised in ~49 sections, nearly all of them passing a bare
 * <img> as prevArrow/nextArrow. Slick renders that markup as-is and only adds
 * a `slick-arrow` class, so the result is an image with a click handler: no
 * button role, not focusable, no keyboard support. Rewriting 49 sections would
 * churn markup the CSS targets by class, so the repairs are layered on here
 * instead — the `slick-arrow` class slick adds gives a reliable hook.
 *
 *   1. Arrows that are not real <button>s get role="button", tabindex="0", a
 *      proper label, and Enter/Space activation. Decorative arrow images are
 *      hidden from AT so the label is not doubled up with alt text like
 *      "Arrow Next". (HOME-01..04, APVR-79/80/84, REEL-165/166)
 *   2. Off-screen slides stop taking keyboard focus. Slick's own initADA only
 *      clears tabindex on 'a, input, button, select' (slick.js:1310) — iframes
 *      are missing, which is why the hidden YouTube slides were still
 *      reachable by Tab. (HOME-11)
 *   3. The carousel is exposed as a labelled group and each slide is numbered,
 *      so the slide count is conveyed. (BULL-67)
 *   4. Slide changes are announced through a polite live region.
 *      (HOME-05, BULL-65, REEL-167)
 *
 * Vanilla JS, no dependencies — it reads slick's DOM output, not its API.
 */
(function () {
  'use strict';

  /* Focusables slick's initADA misses. */
  var EXTRA_FOCUSABLE = 'iframe, object, embed, area[href], audio[controls], video[controls], [contenteditable], [tabindex]:not([tabindex="-1"])';

  function isNext(el) {
    var s = (el.className || '') + ' ' + (el.id || '');
    if (/next|right/i.test(s)) return true;
    if (/prev|left|back/i.test(s)) return false;
    return null;
  }

  function labelArrow(el, fallbackNext) {
    var next = isNext(el);
    if (next === null) next = fallbackNext;
    return next ? 'Next slide' : 'Previous slide';
  }

  /* ---- 1. arrows ---------------------------------------------------------- */
  function enhanceArrow(el, fallbackNext) {
    if (el.dataset.a11yArrow === '1') return;
    el.dataset.a11yArrow = '1';

    var isButton = el.tagName === 'BUTTON';
    if (!isButton) {
      if (!el.getAttribute('role')) el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
          e.preventDefault();
          el.click();
        }
      });
    }
    if (!el.getAttribute('aria-label')) {
      el.setAttribute('aria-label', labelArrow(el, fallbackNext));
    }
    // The arrow graphic is decorative once the control itself is labelled.
    // When the arrow *is* the image, clear its alt so the old "Arrow Next"
    // text cannot surface; when it merely contains one, hide that child.
    if (el.tagName === 'IMG') {
      el.setAttribute('alt', '');
    } else {
      var img = el.querySelector('img');
      if (img) {
        img.setAttribute('alt', '');
        img.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /* ---- 2. off-screen slides must not hold focus ---------------------------- */
  function setSlideFocusability(slide, reachable) {
    var els = slide.querySelectorAll(EXTRA_FOCUSABLE);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (reachable) {
        if (el.dataset.a11yTabPrev !== undefined) {
          if (el.dataset.a11yTabPrev === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', el.dataset.a11yTabPrev);
          delete el.dataset.a11yTabPrev;
        }
      } else if (el.dataset.a11yTabPrev === undefined) {
        el.dataset.a11yTabPrev = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '';
        el.setAttribute('tabindex', '-1');
      }
    }
  }

  /* ---- 3 & 4. grouping and announcements ---------------------------------- */
  function liveRegionFor(slider) {
    var region = slider.querySelector(':scope > .a11y-carousel-status');
    if (!region) {
      region = document.createElement('p');
      region.className = 'a11y-carousel-status visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      slider.appendChild(region);
    }
    return region;
  }

  /* This theme writes section titles as <p class="h2">, not <h2> — so matching
     heading tags alone finds nothing (live check on the homepage: all three
     carousels came out labelled "Carousel"). Match the visual-heading classes
     too, and walk previous siblings outward rather than using closest(), which
     can match the slider itself when it carries an id. */
  var HEADINGISH = 'h1,h2,h3,h4,[class~="h1"],[class~="h2"],[class~="h3"],[class~="h4"],.heading,.section-title';

  function headingTextNear(el) {
    var node = el;
    while (node && node !== document.body) {
      var prev = node.previousElementSibling;
      while (prev) {
        if (prev.matches && prev.matches(HEADINGISH)) {
          var t = (prev.textContent || '').replace(/\s+/g, ' ').trim();
          if (t) return t;
        }
        if (prev.querySelectorAll) {
          var inner = prev.querySelectorAll(HEADINGISH);
          if (inner.length) {
            var last = (inner[inner.length - 1].textContent || '').replace(/\s+/g, ' ').trim();
            if (last) return last;
          }
        }
        prev = prev.previousElementSibling;
      }
      node = node.parentElement;
    }
    return '';
  }

  function sliderLabel(slider) {
    var labelled = slider.getAttribute('aria-label');
    if (labelled) return labelled;
    var text = headingTextNear(slider);
    if (text.length > 80) text = text.slice(0, 77) + '\u2026';
    return text ? text + ' carousel' : 'Carousel';
  }

  /* Two unlabelled banner carousels both announcing "Carousel" are impossible to
     tell apart, so number any label that repeats on the page. */
  function dedupeLabels() {
    var seen = {};
    var sliders = document.querySelectorAll('[aria-roledescription="carousel"]');
    for (var i = 0; i < sliders.length; i++) {
      var base = sliders[i].dataset.a11yBaseLabel || sliders[i].getAttribute('aria-label') || 'Carousel';
      sliders[i].dataset.a11yBaseLabel = base;
      seen[base] = (seen[base] || 0) + 1;
      sliders[i].setAttribute('aria-label', seen[base] > 1 ? base + ' ' + seen[base] : base);
    }
  }

  function enhanceSlider(slider) {
    var slides = slider.querySelectorAll('.slick-slide:not(.slick-cloned)');
    if (!slides.length) return;

    if (slider.dataset.a11yCarousel !== '1') {
      slider.dataset.a11yCarousel = '1';
      if (!slider.getAttribute('role')) slider.setAttribute('role', 'group');
      slider.setAttribute('aria-roledescription', 'carousel');
      if (!slider.getAttribute('aria-label')) slider.setAttribute('aria-label', sliderLabel(slider));
      liveRegionFor(slider);
    }

    var current = -1;
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      if (slide.dataset.a11ySlide !== '1') {
        slide.dataset.a11ySlide = '1';
        slide.setAttribute('aria-roledescription', 'slide');
        if (!slide.getAttribute('aria-label')) {
          slide.setAttribute('aria-label', (i + 1) + ' of ' + slides.length);
        }
      }
      var active = slide.classList.contains('slick-active') || slide.classList.contains('slick-current');
      // A visible slide must never stay hidden from assistive tech (APVR-87).
      if (active && slide.getAttribute('aria-hidden') === 'true') {
        slide.setAttribute('aria-hidden', 'false');
      }
      setSlideFocusability(slide, active);
      if (slide.classList.contains('slick-current')) current = i;
    }

    // Cloned slides are duplicates — never reachable, never announced.
    var clones = slider.querySelectorAll('.slick-cloned');
    for (var c = 0; c < clones.length; c++) setSlideFocusability(clones[c], false);

    if (current >= 0 && String(current) !== slider.dataset.a11yCurrent) {
      var first = slider.dataset.a11yCurrent === undefined;
      slider.dataset.a11yCurrent = String(current);
      // Do not announce the initial position, only genuine changes.
      if (!first) {
        liveRegionFor(slider).textContent = 'Slide ' + (current + 1) + ' of ' + slides.length;
      }
    }
  }

  function sweep() {
    var arrows = document.querySelectorAll('.slick-arrow');
    for (var i = 0; i < arrows.length; i++) {
      // Fall back to DOM order when the class names say nothing: slick appends
      // prev before next.
      enhanceArrow(arrows[i], i % 2 === 1);
    }
    var sliders = document.querySelectorAll('.slick-slider, .slick-initialized');
    for (var j = 0; j < sliders.length; j++) enhanceSlider(sliders[j]);
    dedupeLabels();
  }

  function start() {
    sweep();
    if (!('MutationObserver' in window)) return;
    var queued = false;
    var obs = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; sweep(); });
    });
    obs.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
